import React, { useState } from 'react';
import { F, Icon } from '../theme.jsx';
import { useModalViewport, modalShellStyle, ModalHeader } from '../modal-utils.jsx';
import { useToast } from '../components/boothly/toast-provider.jsx';

// ─────────────────────────────────────────────────────────────
// StaleSessionReminderSheet (P-Polish 1.1)
// Wird gezeigt wenn eine Trödel-Session > 12h alt ist und User die App
// erneut öffnet. Zwei Optionen: jetzt beenden ODER weiterlaufen lassen.
// ─────────────────────────────────────────────────────────────
function StaleSessionReminderSheet({ dark, t, store, session, onDismiss, onEndNow }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const startsIso = session?.starts_at;
  const hours = startsIso ? Math.floor((Date.now() - new Date(startsIso).getTime()) / 3600000) : 0;
  const dateStr = startsIso ? new Date(startsIso).toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric"
  }) : "";
  const timeStr = startsIso ? new Date(startsIso).toLocaleTimeString("de-DE", {
    hour: "2-digit", minute: "2-digit"
  }) : "";
  const accent = store?.type === "flea" ? t.accent.purple : t.accent.green;
  return (
    <>
      <div onClick={onDismiss} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 400, radius: 24, padding: "22px 22px 18px", zIndex: 97 })}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: accent.weak, color: accent.solid,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
          }}>⏰</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px" }}>
              Markt läuft seit {hours} Std
            </div>
            <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>
              Hast du vergessen ihn zu beenden?
            </div>
          </div>
        </div>
        <div style={{
          background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          borderRadius: 12, padding: "10px 14px", marginBottom: 16,
          fontSize: 13, color: t.textSub, lineHeight: 1.5,
        }}>
          {session?.location && <div><b style={{ color: t.text }}>{session.location}</b></div>}
          <div>Gestartet am {dateStr} · {timeStr}</div>
          <div style={{ color: t.textMuted, fontSize: 11, marginTop: 3 }}>
            Trödel · {store?.name || ""}
          </div>
        </div>
        <button onClick={onEndNow} style={{
          width: "100%", padding: "13px",
          background: accent.solid, color: "#fff",
          border: "none", borderRadius: 14,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: "pointer", marginBottom: 8,
          boxShadow: `0 4px 14px ${accent.weak}`,
        }}>Markt jetzt beenden</button>
        <button onClick={onDismiss} style={{
          width: "100%", padding: "11px",
          background: "transparent", border: "none",
          color: t.textMuted,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Weiterlaufen lassen</button>
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────
// CreateAreaSheet (P-Landing-Redesign)
// Auswahl-Dialog "Welcher Bereich?" mit Online / Trödel.
// Trödel wird deaktiviert wenn schon angelegt.
// ─────────────────────────────────────────────────────────────
function CreateAreaSheet({ dark, t, userStores, authToken, onClose, onCreateOnline, onCreateMode, onSelectExisting, onJoinedViaCode }) {
  const toast = useToast();
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const hasFlea   = !!(userStores || []).find(s => s.type === "flea");
  const fleaStore   = (userStores || []).find(s => s.type === "flea");
  // P-5-Punkte-Fix Block 5: Step-State für 2-Schritt-Flow
  // step = "choose" → Hauptauswahl (Erstellen / Beitreten)
  //      = "create" → Mode-Auswahl (Online/Trödel)
  //      = "join"   → Code-Eingabe
  const [step, setStep] = useState("choose");
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState(null);
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    const cleaned = joinCode.trim().toUpperCase().replace(/\s+/g, "");
    if (!cleaned) { setJoinError("Code eingeben"); return; }
    if (joining) return;
    setJoining(true); setJoinError(null);
    try {
      const res = await fetch(`/api/invitations/${encodeURIComponent(cleaned)}/redeem`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setJoinError(data.reason || "Code ungültig oder abgelaufen");
        setJoining(false);
        return;
      }
      // Success: data hat den joined-Store
      toast.success(`${data.store?.name || "Bereich"} beigetreten`);
      onJoinedViaCode && onJoinedViaCode(data.store);
      setJoining(false);
    } catch (e) {
      setJoinError("Netzwerkfehler — versuche erneut");
      setJoining(false);
    }
  };

  const Option = ({ icon, accent, title, subtitle, disabled, disabledReason, onClick }) => (
    <button onClick={!disabled ? onClick : undefined} disabled={disabled} style={{
      width: "100%", padding: "14px 16px",
      background: "transparent", border: "none",
      borderTop: `0.5px solid ${t.divider}`,
      cursor: disabled ? "default" : "pointer",
      fontFamily: F, textAlign: "left",
      display: "flex", alignItems: "center", gap: 12,
      opacity: disabled ? 0.5 : 1,
    }}>
      <span style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: accent.weak, color: accent.solid,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{title}</div>
        <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>
          {disabled ? disabledReason : subtitle}
        </div>
      </div>
      {!disabled && <div style={{ fontSize: 18, color: t.chevron, fontWeight: 200, flexShrink: 0 }}>›</div>}
    </button>
  );

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 420, radius: 22, padding: 0, zIndex: 93 })}>
        {step === "choose" && (
          <>
            <div style={{ padding: "20px 22px 12px" }}>
              <ModalHeader t={t} dark={dark} F={F} title="Neuer Bereich" subtitle="Was möchtest du tun?" onClose={onClose} marginBottom={4} />
            </div>
            <div style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 14px) + 8px)" }}>
              <Option
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>}
                accent={t.accent.blue}
                title="Neuen Bereich erstellen"
                subtitle="Online-Store oder Trödel"
                onClick={() => setStep("create")}
              />
              <Option
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3.54-3.54a5 5 0 0 0-7.07-7.07l-1.41 1.41"/><path d="M14 11a5 5 0 0 0-7.07 0l-3.54 3.54a5 5 0 0 0 7.07 7.07l1.41-1.41"/></svg>}
                accent={t.accent.purple}
                title="Bereich beitreten"
                subtitle="Mit Einladungscode"
                onClick={() => setStep("join")}
              />
            </div>
          </>
        )}

        {step === "create" && (
          <>
            <div style={{ padding: "20px 22px 12px" }}>
              <ModalHeader t={t} dark={dark} F={F}
                title="Bereich erstellen"
                subtitle="Wähle den Typ"
                onClose={onClose}
                marginBottom={4}
              />
              <button onClick={() => setStep("choose")} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "transparent", border: "none", cursor: "pointer",
                color: t.textMuted, fontFamily: F, fontSize: 13, padding: 0,
                marginTop: 4,
              }}>
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                  <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Zurück
              </button>
            </div>
            <div style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 14px) + 8px)" }}>
              <Option
                icon={Icon.store ? Icon.store(18) : Icon.bars(18)}
                accent={t.accent.blue}
                title="Online-Store"
                subtitle="eBay, Etsy, Website-Bestellungen"
                onClick={onCreateOnline}
              />
              <Option
                icon={Icon.tag(18)}
                accent={t.accent.purple}
                title="Trödel"
                subtitle="Flohmarkt mit Einzelstücken"
                disabled={hasFlea}
                disabledReason={fleaStore ? `Bereits angelegt — tippen um zu öffnen` : "Bereits angelegt"}
                onClick={hasFlea
                  ? () => onSelectExisting(fleaStore)
                  : () => onCreateMode("flea")
                }
              />
            </div>
          </>
        )}

        {step === "join" && (
          <div style={{ padding: "20px 22px calc(env(safe-area-inset-bottom, 14px) + 18px)" }}>
            <ModalHeader t={t} dark={dark} F={F}
              title="Bereich beitreten"
              subtitle="Einladungscode eingeben"
              onClose={onClose}
              marginBottom={4}
            />
            <button onClick={() => { setStep("choose"); setJoinError(null); }} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "transparent", border: "none", cursor: "pointer",
              color: t.textMuted, fontFamily: F, fontSize: 13, padding: 0,
              marginTop: 4, marginBottom: 16,
            }}>
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Zurück
            </button>

            <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
              Einladungscode
            </div>
            <div style={{
              background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              borderRadius: 12, padding: "0 14px",
              marginBottom: 8,
              border: `1px solid ${joinError ? t.accent.red.solid + "60" : t.glassBorder}`,
            }}>
              <input
                autoFocus type="text"
                value={joinCode}
                onChange={e => { setJoinCode(e.target.value); setJoinError(null); }}
                placeholder="z.B. MEMOR-K7Q9-X3F2"
                style={{
                  width: "100%", background: "transparent", border: "none", outline: "none",
                  padding: "13px 0", fontSize: 16, color: t.text, fontFamily: F,
                  letterSpacing: "0.05em", textTransform: "uppercase",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </div>
            {joinError ? (
              <div style={{ fontSize: 12, color: t.accent.red.solid, marginBottom: 12, paddingLeft: 4 }}>
                {joinError}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: t.textSub, marginBottom: 16, lineHeight: 1.5 }}>
                Du wirst Mitglied dieses Bereichs. Der Inhaber muss dich vorab einladen.
              </div>
            )}
            <button onClick={handleJoin} disabled={joining} style={{
              width: "100%", padding: "14px",
              background: t.accent.purple.solid, color: "#fff",
              border: "none", borderRadius: 14,
              fontFamily: F, fontWeight: 700, fontSize: 15,
              cursor: joining ? "default" : "pointer",
              opacity: joining ? 0.6 : 1,
            }}>{joining ? "Trete bei…" : "Beitreten"}</button>
          </div>
        )}
      </div>
    </>
  );
}

export { StaleSessionReminderSheet, CreateAreaSheet };
